import {
  ChevronRight,
  Gift,
  KeyRound,
  UserRoundSearch,
  type LucideIcon,
} from 'lucide-react';
import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { cn, ghostAccentIconTileClass } from '@up2stay/up2promo-ui';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import {
  SettingsGroupRow,
  SettingsGroupRows,
} from '@/core/components/settings/settings-group';
import { SettingsSection } from '@/core/components/settings/settings-section';
import { useIsPlatformAdmin } from '@/core/hooks/use-is-platform-admin';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';

const HUB_LABEL_CLASS = 'text-[15px] font-medium leading-snug text-foreground';

const HUB_ROW_CLASS =
  'flex min-h-11 w-full items-center gap-3 px-4 py-3 text-left transition-[color,background-color] duration-150 hover:bg-[color:var(--ghost-accent-bg)]/40 sm:px-5';

function HubIconTile({ children }: { children: ReactNode }) {
  return (
    <span
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-[8px]',
        ghostAccentIconTileClass,
      )}
    >
      {children}
    </span>
  );
}

function SupportToolsBreadcrumb({
  domainLabel,
  pageLabel,
}: {
  domainLabel: string;
  pageLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {pageLabel}
    </>
  );
}

function ToolRow({
  icon: Icon,
  label,
  onNavigate,
  separated,
}: {
  icon: LucideIcon;
  label: string;
  onNavigate: () => void;
  separated?: boolean;
}) {
  return (
    <SettingsGroupRow separated={separated} className="p-0">
      <button type="button" onClick={onNavigate} className={HUB_ROW_CLASS}>
        <HubIconTile>
          <Icon className="size-4" />
        </HubIconTile>
        <span className={HUB_LABEL_CLASS}>{label}</span>
        <ChevronRight className="ml-auto size-4 shrink-0 text-muted-foreground" />
      </button>
    </SettingsGroupRow>
  );
}

export function SupportToolsHubPage() {
  const dict = useDictionary();
  const pageDict = dict.support.tools;
  const navDict = dict.nav;
  const isPlatformAdmin = useIsPlatformAdmin();
  const { navigateShell } = useNavigateShell();

  if (!isPlatformAdmin) {
    return <Navigate to={shellPaths.moreHub} replace />;
  }

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="gap-6 pb-8">
          <PageHeader
            breadcrumb={
              <SupportToolsBreadcrumb
                domainLabel={navDict.more}
                pageLabel={navDict.supportTools}
              />
            }
            title={pageDict.title}
            backHref={shellPaths.moreHub}
          />
          <SettingsSection
            id="support-tools"
            title={pageDict.sectionTitle}
            grouped
            footer={pageDict.description}
          >
            <SettingsGroupRows>
              <ToolRow
                icon={UserRoundSearch}
                label={navDict.supportImpersonate}
                onNavigate={() =>
                  navigateShell(shellPaths.moreSupportImpersonate)
                }
              />
              <ToolRow
                icon={Gift}
                label={navDict.billingGrants}
                onNavigate={() => navigateShell(shellPaths.moreBillingGrants)}
                separated
              />
              <ToolRow
                icon={KeyRound}
                label={navDict.supportPassword}
                onNavigate={() => navigateShell(shellPaths.moreSupportPassword)}
                separated
              />
            </SettingsGroupRows>
          </SettingsSection>
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
